import { useClient } from 'hooks/useClient';

export type FeatureFlagScope = 'dashboard' | 'runtime' | 'editor';
export type FeatureFlagStatus = 'development' | 'deployed';

export type FeatureFlag = {
  id: string;
  name: string;
  description: string;
  scope: FeatureFlagScope;
  status: FeatureFlagStatus;
  createdAt: string;
  branch?: string;
};

type CreateFlagPayload = {
  name: string;
  description: string;
  scope: FeatureFlagScope;
};

export const useFeatureFlags = () => {
  const client = useClient();

  const fetchBranches = async (scope: FeatureFlagScope) => {
    return client.get<string[]>(`/feature-flags/branches?scope=${scope}`);
  };

  const getFlag = async (id: string) => {
    return client.get<FeatureFlag>(`/feature-flags/${id}`);
  };

  const createFlag = async (payload: CreateFlagPayload) => {
    return client.post<FeatureFlag>('/feature-flags', payload);
  };

  const updateFlag = async (id: string, payload: Partial<FeatureFlag>) => {
    return client.patch<FeatureFlag>(`/feature-flags/${id}`, payload);
  };

  const listFlags = async () => {
    return client.get<FeatureFlag[]>('/feature-flags');
  };

  const createBranch = async (id: string, branch: string) => {
    return client.post<FeatureFlag>(`/feature-flags/${id}/branch`, { branch });
  };

  const getCurrentBranch = async () => {
    return client.get<{ branch: string }>('/feature-flags/current-branch');
  };

  const checkoutBranch = async (branch: string) => {
    return client.post<{ branch: string }>('/feature-flags/checkout', { branch });
  };

  return { listFlags, fetchBranches, getFlag, createFlag, updateFlag, createBranch, getCurrentBranch, checkoutBranch };
};
